<script>
import Layout from '../../layouts/main';
import Uppy from "@uppy/core";
import AwsS3Multipart from "@uppy/aws-s3-multipart";
import {
  createUppyS3MultipartHandlers,
  UPLOAD_CHUNK_SIZE_LARGE,
} from '@/utils/uppyS3Multipart';

import '@uppy/core/dist/style.min.css';
import Content from "@/services/Content";
import Swal from 'sweetalert2';
import ModalEditCropStats from '@/components/modals/news/ModalEditCropStats';
import ModalPreviewCrop from '@/components/modals/news/ModalPreviewCrop';
import ModalCutPreview from '@/components/modals/news/ModalCutPreview';
import ModalSharePost from '@/components/modals/news/ModalSharePost';

// Number of amplitude buckets drawn across the waveform canvas width.
// Higher = finer detail but more draw calls.
const WAVE_RES = 1000;

export default {
  name: 'VideoEditor',
  components: { Layout, ModalEditCropStats, ModalPreviewCrop, ModalCutPreview, ModalSharePost },

  page: {
    title: 'Video Editor',
    meta: [{ name: 'description', content: 'Browser-based video cut editor' }],
  },

  data() {
    return {
      // Uppy
      uppy: null,
      uploadProgress: 0,
      uploading: false,
      selectedFile: null, // keep a reference to the raw File

      // Video
      videoSrc: null,
      videoDuration: 0,
      currentTime: 0,
      isPlaying: false,

      // URL fetch
      urlInput: '',
      urlLoading: false,
      urlProgress: 0,
      urlError: null,

      // Waveform (AnalyserNode canvas — works for any file size)
      audioCtx: null,
      analyser: null,
      animFrame: null,
      waveformSamples: null, // Float32Array(WAVE_RES) filled progressively

      // Cuts
      cuts: [],
      nextId: 1,
      selectedCutId: null,
      pendingCutStart: null,

      // Playing a cut
      playingCutId: null,
      playCheckInterval: null,

      // Inline edit
      editingCutId: null,
      editDescription: '',
      editHashtagStr: '',
      editSavedCutId: null,
      // Inline "Start/End set" confirmation banner, shown briefly inside a cut card
      timeSetCutId: null,
      timeSetField: null,
      timeSetTime: '',

      // Publish panel
      publishingCutId: null,

      // Thumbnails — keyed by cut.id
      thumbnails: {},

      // Cut preview modal
      previewCut: null,

      // Cutting mode: 'manual' = user defines cuts; 'auto' = backend handles it
      cuttingMode: 'manual',

      // True while the API processVideo call is in flight (upload uses `uploading`)
      processing: false,


      // Import / export
      showImport: true,
      importText: '',
      importError: null,
      importSuccess: null,

      // Project cuts (loaded via process_id query param)
      projectCuts: [],
      loadingProjectCuts: false,
      projectCutsError: null,
      refreshing: false,
      cropEditCut: null,
      cropPreviewCut: null,
      shareCut: null,
      videoUrlRefreshTimer: null,

      // Process metadata (loaded when arriving with process_id)
      processType: null,
      processStatus: null,
      processS3Key: null,
    };
  },
  mounted() {
    const multipartHandlers = createUppyS3MultipartHandlers('editor');

    this.uppy = new Uppy({
      restrictions:{
        maxNumberOfFiles: 1,
        allowedFileTypes: ['video/*'],
      },
    })
        .use(AwsS3Multipart, {
          ...multipartHandlers,
          getChunkSize: () => UPLOAD_CHUNK_SIZE_LARGE,
        })
    this.uppy.on('progress', (progress) => {
      this.uploadProgress = progress; // 0-100
    });

    this.uppy.on('complete', (result) => {
      this.uploading = false;
      // console.log('[VideoEditor] Upload took:', this.formatDuration((performance.now() - this._debugUploadStart) / 1000));
      if (result.successful.length) {
        const s3Key = result.successful[0].s3Multipart.key;
        this.submitForProcessing(s3Key);
      }
    });

    this.uppy.on('upload-error', async () => {
      this.uploading = false;
      this.unlockUnload();
      await Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: 'The video could not be uploaded to storage. Please try again.',
        confirmButtonText: 'OK',
      });
      // Keep the editor data intact on failure so the user can retry without
      // re-loading the video and re-entering the cuts.
    });

    // Set up canvas sizing + start the rAF draw loop
    this.$nextTick(() => {
      this.setupWaveformCanvas();
      this.startDrawLoop();
    });

    // Load existing project cuts + presigned video URL if arriving from video-projects page
    if (this.processId) {
      this.loadProcessType();
      this.loadProjectCuts();
      this.loadVideoUrl();
    }
  },

  computed: {
    processId() {
      return this.$route && this.$route.query && this.$route.query.process_id
          ? this.$route.query.process_id
          : null;
    },

    cutsLength() {
      return this.cuts.length;
    },

    publishedCuts() {
      return this.cuts.filter(c => c.published);
    },

    // Cuts that will be sent to the API — rejected ones are excluded
    activeCuts() {
      return this.cuts.filter(c => !c.rejected);
    },

    waveformCuts() {
      return this.processId
        ? [...this.projectCuts, ...this.cuts].filter(c => !c.rejected)
        : this.cuts.filter(c => !c.rejected);
    },

    cutsAddingLocked() {
      if (!this.processType) return false;
      const t = this.processType.toLowerCase();
      return t === 'auto' || t === 'ai';
    },

    isManualProcess() {
      return !!this.processType && this.processType.toLowerCase() === 'manual';
    },

    isProcessCompleted() {
      return !!this.processStatus && this.processStatus.toLowerCase() === 'completed';
    },

    // Auto/AI mode: valid as soon as a file is selected (no cuts needed).
    // Manual mode: need at least one active cut and every active cut must have a title.
    cutsAllValid() {
      if (this.cuttingMode === 'auto') return true;
      return this.activeCuts.length > 0 &&
          this.activeCuts.every(c =>
              c.label && c.label.trim().length > 0 &&
              c.eventType && c.eventType.trim().length > 0 &&
              c.crop_stats != null
          );
    },

    cutsWithoutCrop() {
      if (this.cuttingMode === 'auto') return [];
      return this.activeCuts.filter(c => c.crop_stats == null);
    },

    processButtonTitle() {
      if (!this.selectedFile && !this.processId) return '';
      if (this.cuttingMode === 'manual') {
        if (!this.activeCuts.length) return 'Add at least one cut before processing';
        if (this.cutsWithoutCrop.length) return `${this.cutsWithoutCrop.length} cut(s) are missing crop settings`;
        if (!this.cutsAllValid) return 'All cuts must have a title and event type before processing';
      }
      return '';
    },
  },

  watch: {
    // Regenerate thumbnails only when count changes; also force waveform redraw
    cutsLength() {
      this.generateThumbnails();
      this._invalidateBarCache();
      this._lastDrawTime = -1;
    },

    'projectCuts.length'() {
      this._invalidateBarCache();
      this._lastDrawTime = -1;
    },

    videoSrc() {
      this.generateThumbnails();
    },

    // Scroll selected cut into view
    selectedCutId(newId) {
      if (!newId) return;
      this.$nextTick(() => {
        const ref = this.$refs['cut-card-' + newId];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    },
  },

  beforeDestroy() {
    // Stop rAF draw loop
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = null;
    }
    // Disconnect canvas ResizeObserver
    if (this._waveRO) {
      this._waveRO.disconnect();
      this._waveRO = null;
    }
    // Tear down audio graph
    if (this.audioCtx) {
      this.audioCtx.close();
      this.audioCtx = null;
      this.analyser = null;
    }
    this.unlockUnload();
    if (this.videoSrc && this.videoSrc.startsWith('blob:')) URL.revokeObjectURL(this.videoSrc);
    clearInterval(this.playCheckInterval);
    clearTimeout(this.videoUrlRefreshTimer);
    if (this.uppy) this.uppy.close();
  },

  methods: {
    async processVideo() {
      // In project mode the video is already in S3 — skip the upload entirely
      if (this.processId) {
        this.lockUnload();
        await this.submitForProcessing(null);
        return;
      }
      if (!this.selectedFile) return;
      if (this.cuttingMode === 'manual' && !this.cuts.length) return;

      // Remove all files from the queue without aborting any in-flight
      // multipart upload on S3 — cancelAll() was causing uploadId mismatches
      this.uppy.getFiles().forEach(f => this.uppy.removeFile(f.id));
      this.uploadProgress = 0;

      this.uppy.addFile({
        name: this.selectedFile.name,
        type: this.selectedFile.type,
        data: this.selectedFile,
      });

      this._debugUploadStart = performance.now();
      this.lockUnload();
      this.uploading = true;
      await this.uppy.upload();
    },

    async submitForProcessing(s3Key) {
      const isAuto = this.cuttingMode === 'auto';

      const serializeCut = (cut, includeCutId) => {
        const s = parseFloat(cut.start);
        const e = parseFloat(cut.end);
        const obj = {
          start: s,
          end: e,
          clip_duration: Math.round((e - s) * 1000) / 1000,
          title: cut.label || '',
          event_type: cut.eventType || '',
          description: cut.description || '',
          hashtags: cut.hashtags || [],
          ...(cut.crop_stats ? { crop_stats: cut.crop_stats } : {}),
        };
        if (includeCutId) {
          obj.cut_id = cut.id;
          if (cut.rejected) obj.rejected = true;
        }
        return obj;
      };

      let cuts;
      if (isAuto) {
        cuts = [];
      } else if (this.processId) {
        // Existing project: all project cuts are sent with rejected flag so the backend
        // can act on rejections; newly added cuts are still filtered client-side
        cuts = [
          ...this.projectCuts.map(c => serializeCut(c, true)),
          ...this.cuts.filter(c => !c.rejected).map(c => serializeCut(c, false)),
        ];
      } else {
        cuts = this.activeCuts.map(c => serializeCut(c, false));
      }

      const cutCount = cuts.length;
      const payload = {
        s3_key: s3Key || this.processS3Key,
        type: this.cuttingMode,
        ...(this.processId ? { process_id: this.processId } : {}),
        cuts,
      };

      this.processing = true;
      const processStart = performance.now();
      try {
        await Content.processVideo(payload);
        // console.log('[VideoEditor] API call took:', this.formatDuration((performance.now() - processStart) / 1000));
        this.processing = false;
        this.unlockUnload();

        if (this.processId) {
          await Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: `Process cuts have been updated successfully.`,
            confirmButtonText: 'Done',
            confirmButtonColor: '#1D9E75',
          });
          this.cuts = [];
          this.refreshing = true;
          await Promise.all([
            this.loadProcessType(),
            this.loadProjectCuts(),
            this.loadVideoUrl(),
          ]);
          this.refreshing = false;
        } else {
          await Swal.fire({
            icon: 'success',
            title: 'Processing Started!',
            html: isAuto
                ? `Your video has been uploaded.<br><br>The backend will <strong>automatically</strong> detect and process clips shortly.`
                : `Your video has been uploaded successfully.<br><br><strong>${cutCount}</strong> clip${cutCount !== 1 ? 's' : ''} will be processed shortly.`,
            confirmButtonText: 'Done',
            confirmButtonColor: '#1D9E75',
          });
          this.resetEditor();
        }
      } catch (err) {
        // console.log('[VideoEditor] API call took:', this.formatDuration((performance.now() - processStart) / 1000));
        this.processing = false;
        this.unlockUnload();
        await Swal.fire({
          icon: 'error',
          title: this.processId ? 'Update Failed' : 'Processing Failed',
          text: err?.response?.data?.error || (this.processId ? 'Failed to update the process. Please try again.' : 'Failed to start video processing. Please try again.'),
          confirmButtonText: 'OK',
        });
        // Keep the editor data intact on failure so the user can fix the issue and retry
        // without re-entering the video and cuts.
      }
    },

    lockUnload() {
      if (this.beforeUnloadFn) return; // already locked
      this.beforeUnloadFn = (e) => {
        e.preventDefault();
        e.returnValue = ''; // required for Chrome to show the dialog
      };
      window.addEventListener('beforeunload', this.beforeUnloadFn);
    },

    unlockUnload() {
      if (!this.beforeUnloadFn) return;
      window.removeEventListener('beforeunload', this.beforeUnloadFn);
      this.beforeUnloadFn = null;
    },

    resetEditor() {
      // Stop any cut playback
      clearInterval(this.playCheckInterval);
      this.playingCutId = null;

      // Clear waveform samples (keep audioCtx/analyser alive —
      // createMediaElementSource can only be called once per element)
      this.waveformSamples = null;
      this._lastDrawTime = -1; // force a redraw on next frame

      // Release blob URL (presigned S3 URLs must not be revoked)
      if (this.videoSrc && this.videoSrc.startsWith('blob:')) URL.revokeObjectURL(this.videoSrc);

      // Reset all state
      this.videoSrc        = null;
      this.videoDuration   = 0;
      this.currentTime     = 0;
      this.isPlaying       = false;
      this.selectedFile    = null;
      this.cuts            = [];
      this.nextId          = 1;
      this.selectedCutId   = null;
      this.pendingCutStart = null;
      this.editingCutId    = null;
      this.publishingCutId = null;
      this.thumbnails      = {};
      this.uploadProgress  = 0;
      this.uploading       = false;
      this.processing      = false;
      this.urlInput        = '';
      this.urlError        = null;

    },

    // ─── Video source ─────────────────────────────────────────────────────────

    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.selectedFile = file;
      this.loadVideoBlob(URL.createObjectURL(file));
      e.target.value = '';
    },

    async loadFromUrl() {
      const url = this.urlInput.trim();
      if (!url) return;
      this.urlLoading = true;
      this.urlError = null;
      this.urlProgress = 0;

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : null;
        const reader = response.body.getReader();
        const chunks = [];
        let received = 0;

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.length;
          if (total) this.urlProgress = Math.round((received / total) * 100);
        }

        const mimeType = response.headers.get('content-type') || 'video/mp4';
        const blob = new Blob(chunks, { type: mimeType });
        this.loadVideoBlob(URL.createObjectURL(blob));
      } catch (err) {
        if (err instanceof TypeError) {
          this.urlError = 'Failed to load video — this is likely a CORS issue. The server does not allow cross-origin requests.';
        } else {
          this.urlError = err.message;
        }
      } finally {
        this.urlLoading = false;
      }
    },

    loadVideoBlob(blobUrl) {
      if (this.videoSrc) URL.revokeObjectURL(this.videoSrc);
      if (this.$refs.video) this.$refs.video.pause();
      this.isPlaying = false;
      this.currentTime = 0;
      this.videoDuration = 0;
      this.pendingCutStart = null;
      this.waveformSamples = null; // clear old waveform data
      this._lastDrawTime = -1;    // force a redraw on next frame
      this.videoSrc = blobUrl;
      // Init audio analyser on first load (user gesture is guaranteed here).
      // Subsequent calls are no-ops because createMediaElementSource can only
      // be called once per HTMLMediaElement.
      this.$nextTick(() => this.initAudioAnalyser());
    },

    // ─── Video events ─────────────────────────────────────────────────────────

    onLoadedMetadata() {
      this.videoDuration = this.$refs.video.duration;
    },

    onTimeUpdate() {
      // Reading video.currentTime on every timeupdate (fires many times/sec while
      // scrubbing) triggers a full Vue re-render each time. Throttle reactive updates
      // to ~4fps — the canvas rAF loop reads $refs.video.currentTime directly so
      // the playhead stays frame-accurate regardless.
      const now = performance.now();
      if (now - (this._lastTimeUpdate || 0) < 250) return;
      this._lastTimeUpdate = now;
      this.currentTime = this.$refs.video.currentTime;
    },

    onVideoEnded() {
      this.isPlaying = false;
      if (this.playingCutId !== null) {
        this.playingCutId = null;
        clearInterval(this.playCheckInterval);
      }
    },

    togglePlay() {
      if (!this.$refs.video) return;
      this.isPlaying ? this.$refs.video.pause() : this.$refs.video.play();
    },

    seekTo(time) {
      if (!this.$refs.video) return;
      this.$refs.video.currentTime = time;
    },

    onSeekBarInput(e) {
      this.seekTo(parseFloat(e.target.value));
    },

    onWaveformClick(e) {
      if (!this.videoDuration) return;
      const canvas = this.$refs.waveformCanvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const ratio = (e.clientX - rect.left) / rect.width;
      const time = Math.max(0, Math.min(ratio * this.videoDuration, this.videoDuration));
      this.seekTo(time);

      // If the click lands inside a cut's range, select it in the right panel
      const hit = this.waveformCuts.find(c => time >= c.start && time <= c.end);
      if (hit) this.selectedCutId = hit.id;
    },

    // ─── Web Audio waveform (AnalyserNode canvas) ─────────────────────────────
    // Uses createMediaElementSource → AnalyserNode.  No full-file decode.
    // Waveform data fills progressively as the user plays/scrubs the video.
    // Works for any file size — no RAM explosion.

    // Measure the canvas once and watch for CSS resizes via ResizeObserver.
    // This avoids reading offsetWidth/offsetHeight (layout-forcing) in the hot rAF path.
    setupWaveformCanvas() {
      const canvas = this.$refs.waveformCanvas;
      if (!canvas) return;
      this._waveW = canvas.offsetWidth  || 300;
      this._waveH = canvas.offsetHeight || 80;
      canvas.width  = this._waveW;
      canvas.height = this._waveH;
      this._invalidateBarCache();
      if (window.ResizeObserver) {
        if (this._waveRO) this._waveRO.disconnect();
        this._waveRO = new ResizeObserver(entries => {
          const { width, height } = entries[0].contentRect;
          this._waveW = Math.round(width)  || 300;
          this._waveH = Math.round(height) || 80;
          const c = this.$refs.waveformCanvas;
          if (c) { c.width = this._waveW; c.height = this._waveH; }
          this._invalidateBarCache();
          this._lastDrawTime = -1;
        });
        this._waveRO.observe(canvas);
      }
    },

    // Mark the bar cache as stale — called when samples or canvas size changes
    _invalidateBarCache() {
      this._barCacheDirty = true;
    },

    initAudioAnalyser() {
      if (this.analyser) return; // already connected
      const video = this.$refs.video;
      if (!video) return;
      try {
        this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const source = this.audioCtx.createMediaElementSource(video);
        this.analyser = this.audioCtx.createAnalyser();
        this.analyser.fftSize = 256;
        source.connect(this.analyser);
        this.analyser.connect(this.audioCtx.destination);
        // Pre-allocate the sample buffer once — avoids per-frame heap allocation
        this._analyserBuf = new Uint8Array(this.analyser.frequencyBinCount);
      } catch (e) {
        // Non-fatal — waveform stays as placeholder
        console.warn('[VideoEditor] Web Audio init failed:', e.message);
      }
    },

    // rAF loop: sample live audio + redraw canvas every frame
    startDrawLoop() {
      if (this.animFrame) cancelAnimationFrame(this.animFrame);
      const loop = () => {
        this.sampleAudio();
        this.drawWaveform();
        this.animFrame = requestAnimationFrame(loop);
      };
      this.animFrame = requestAnimationFrame(loop);
    },

    // Read current amplitude from AnalyserNode and store in waveformSamples
    sampleAudio() {
      if (!this.analyser || !this.videoDuration || !this.isPlaying) return;

      // Ensure AudioContext is running (Chrome suspends until user gesture)
      if (this.audioCtx.state === 'suspended') this.audioCtx.resume();

      // Reuse pre-allocated buffer — no heap allocation per frame
      this.analyser.getByteTimeDomainData(this._analyserBuf);

      // Peak amplitude this frame (0–1)
      let peak = 0;
      const buf = this._analyserBuf;
      for (let i = 0; i < buf.length; i++) {
        const v = Math.abs((buf[i] / 128.0) - 1.0);
        if (v > peak) peak = v;
      }

      // Read directly from the element — not the throttled reactive prop
      const liveTime = this.$refs.video ? this.$refs.video.currentTime : this.currentTime;

      // Map playhead position → bucket index
      const slot = Math.floor((liveTime / this.videoDuration) * WAVE_RES);
      if (slot >= 0 && slot < WAVE_RES) {
        if (!this.waveformSamples) {
          this.waveformSamples = new Float32Array(WAVE_RES);
        }
        // Keep the highest peak seen for this bucket
        if (peak > this.waveformSamples[slot]) {
          this.waveformSamples[slot] = peak;
          this._invalidateBarCache(); // bar layer needs a re-render
        }
      }
    },

    // Paint background + cut overlays + waveform bars onto an offscreen canvas.
    // Only re-runs when samples or canvas size change; scrubbing skips it entirely.
    renderBarCache() {
      const W = this._waveW || 300;
      const H = this._waveH || 80;

      if (!this._barCanvas) {
        this._barCanvas = document.createElement('canvas');
      }
      this._barCanvas.width  = W;
      this._barCanvas.height = H;
      const ctx = this._barCanvas.getContext('2d');

      // Background
      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, W, H);

      if (!this.videoDuration) {
        ctx.fillStyle = 'rgba(255,255,255,0.10)';
        ctx.fillRect(0, H / 2 - 1, W, 2);
        return;
      }

      // ── Cut segment overlays ──
      const cutsToRender = this.waveformCuts;
      if (cutsToRender && cutsToRender.length) {
        cutsToRender.forEach(cut => {
          const x1 = Math.round((cut.start / this.videoDuration) * W);
          const x2 = Math.round((cut.end   / this.videoDuration) * W);
          ctx.fillStyle = this.confidenceColor(cut.confidence);
          ctx.fillRect(x1, 0, Math.max(2, x2 - x1), H);
        });
      }

      // ── Waveform bars — two batched path fills instead of 2000 fillRect calls ──
      const samples = this.waveformSamples;
      const barW    = W / WAVE_RES;
      const minBarW = Math.max(1, barW - 0.5);
      const midY    = H / 2;

      // Dim ticks for unvisited buckets
      ctx.beginPath();
      for (let i = 0; i < WAVE_RES; i++) {
        if (!samples || samples[i] <= 0.01) {
          ctx.rect(i * barW, midY - 1, minBarW, 2);
        }
      }
      ctx.fillStyle = 'rgba(255,255,255,0.08)';
      ctx.fill();

      // Bright bars for visited buckets
      ctx.beginPath();
      for (let i = 0; i < WAVE_RES; i++) {
        if (samples && samples[i] > 0.01) {
          const barH = Math.max(2, samples[i] * H * 0.88);
          ctx.rect(i * barW, midY - barH / 2, minBarW, barH);
        }
      }
      ctx.fillStyle = 'rgba(255,255,255,0.72)';
      ctx.fill();

      this._barCacheDirty = false;
    },

    // Composite frame: cached bar layer + dynamic overlays (playhead, pending marker).
    // Scrubbing only redraws the ~5 dynamic elements — the 2000-bar layer is reused.
    drawWaveform() {
      const canvas = this.$refs.waveformCanvas;
      if (!canvas) return;

      const W = this._waveW || 300;
      const H = this._waveH || 80;

      // Always read live time directly — bypasses the throttled reactive prop
      const t  = this.$refs.video ? this.$refs.video.currentTime : this.currentTime;
      const cl = this.waveformCuts.length;
      const ps = this.pendingCutStart;

      // Skip entirely when nothing has changed
      if (!this.isPlaying && !this._barCacheDirty &&
          t === this._lastDrawTime && cl === this._lastDrawCuts && ps === this._lastDrawPending) return;

      this._lastDrawTime    = t;
      this._lastDrawCuts    = cl;
      this._lastDrawPending = ps;

      // Also invalidate bar cache when cuts change
      if (cl !== this._lastCachecuts) {
        this._lastCachecuts = cl;
        this._barCacheDirty = true;
      }

      // Re-render the bar layer only when needed
      if (this._barCacheDirty) this.renderBarCache();

      const ctx = canvas.getContext('2d');

      // Stamp the cached bar layer — single GPU texture copy
      ctx.drawImage(this._barCanvas, 0, 0);

      if (!this.videoDuration) return;

      // ── Pending cut-start marker ──
      if (ps !== null) {
        const sx = Math.round((ps / this.videoDuration) * W);
        ctx.strokeStyle = '#fd7e14';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx, H);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.fillStyle = '#fd7e14';
        ctx.beginPath();
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx + 8, 0);
        ctx.lineTo(sx, 10);
        ctx.closePath();
        ctx.fill();
      }

      // ── Playhead ──
      const px = (t / this.videoDuration) * W;
      ctx.strokeStyle = '#f0c040';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px, H);
      ctx.stroke();
    },

    // ─── Cut controls ─────────────────────────────────────────────────────────

    setCutStart() {
      // Read live time so the cut point is exact even between throttled Vue updates
      this.pendingCutStart = this.$refs.video ? this.$refs.video.currentTime : this.currentTime;
    },

    setCutEnd() {
      if (this.pendingCutStart === null) return;
      const start = this.pendingCutStart;
      const end = this.$refs.video ? this.$refs.video.currentTime : this.currentTime;
      if (end <= start) return;
      this.addCut({
        start,
        end,
        label: '',
        confidence: null,
        eventType: '',
        description: '',
        hashtags: [],
        triggerTime: null,
        published: false,
        rejected: false,
      });
      this.pendingCutStart = null;
    },

    addNewCut() {
      const start = this.$refs.video ? this.$refs.video.currentTime : this.currentTime;
      this.addCut({
        start,
        end: start,
        label: '',
        confidence: null,
        eventType: '',
        description: '',
        hashtags: [],
        triggerTime: null,
        published: false,
        rejected: false,
      });
    },

    addCut(data) {
      const cut = Object.assign({ id: this.nextId++ }, data);
      this.cuts.push(cut);
      this.selectedCutId = cut.id;
    },

    selectCut(cut) {
      this.selectedCutId = cut.id;
    },

    selectCutAndSeek(cut) {
      this.selectedCutId = cut.id;
      this.seekTo(cut.start);
    },

    toggleRejectCut(cut) {
      this.$set(cut, 'rejected', !cut.rejected);
      // Close any open panels on the cut when rejecting
      if (cut.rejected) {
        if (this.editingCutId   === cut.id) this.editingCutId   = null;
        if (this.publishingCutId === cut.id) this.publishingCutId = null;
      }
    },

    deleteCut(cut) {
      const idx = this.cuts.findIndex(c => c.id === cut.id);
      if (idx !== -1) this.cuts.splice(idx, 1);
      if (this.selectedCutId === cut.id) this.selectedCutId = null;
      if (this.editingCutId === cut.id) this.editingCutId = null;
      if (this.publishingCutId === cut.id) this.publishingCutId = null;
      if (this.playingCutId === cut.id) {
        clearInterval(this.playCheckInterval);
        this.playingCutId = null;
      }
      this.$delete(this.thumbnails, cut.id);
    },

    clearAllCuts() {
      this.cuts = [];
      this.selectedCutId = null;
      this.editingCutId = null;
      this.publishingCutId = null;
      clearInterval(this.playCheckInterval);
      this.playingCutId = null;
      this.thumbnails = {};
    },

    // ─── Play cut ─────────────────────────────────────────────────────────────

    togglePlayCut(cut) {
      if (!this.$refs.video) return;

      if (this.playingCutId === cut.id) {
        this.$refs.video.pause();
        this.playingCutId = null;
        clearInterval(this.playCheckInterval);
        return;
      }

      // Stop any previous cut playback
      if (this.playingCutId !== null) {
        clearInterval(this.playCheckInterval);
        this.$refs.video.pause();
        this.playingCutId = null;
      }

      this.$refs.video.currentTime = cut.start;
      this.$refs.video.play();
      this.playingCutId = cut.id;

      this.playCheckInterval = setInterval(() => {
        if (!this.$refs.video) return;
        if (this.$refs.video.currentTime >= cut.end) {
          this.$refs.video.pause();
          this.playingCutId = null;
          clearInterval(this.playCheckInterval);
        }
      }, 100);
    },

    // ─── Inline edit ──────────────────────────────────────────────────────────

    toggleEditCut(cut) {
      if (this.editingCutId === cut.id) {
        this.editingCutId = null;
        return;
      }
      this.publishingCutId = null;
      this.editingCutId = cut.id;
      this.editDescription = cut.description || '';
      this.editHashtagStr  = (cut.hashtags || []).join(', ');
    },

    // Update a cut in whichever array (projectCuts or cuts) contains it.
    // Both arrays hold already-observed reactive objects, so direct property
    // mutation via Object.assign goes through Vue's setters and triggers re-renders.
    _updateCut(cutId, updates) {
      const cut =
        this.projectCuts.find(c => c.id === cutId) ||
        this.cuts.find(c => c.id === cutId);
      if (!cut) return;
      Object.keys(updates).forEach(key => this.$set(cut, key, updates[key]));
      if ('start' in updates || 'end' in updates) {
        this._invalidateBarCache();
        this._lastDrawTime = -1;
      }
    },

    // Apply a time field immediately — no Save needed
    applyCutTime(cut, field, rawValue) {
      const time = typeof rawValue === 'number' ? rawValue : this.parseTimeStr(rawValue);
      if (time === null || isNaN(time) || time < 0) return;
      this._updateCut(cut.id, { [field]: time });
      // Show a brief info banner inside this cut's card confirming the set + next step.
      this.timeSetCutId = cut.id;
      this.timeSetField = field;
      this.timeSetTime  = this.formatTime(time);
      clearTimeout(this._timeSetTimer);
      this._timeSetTimer = setTimeout(() => {
        this.timeSetCutId = null;
        this.timeSetField = null;
        this.timeSetTime  = '';
      }, 4000);
    },

    // Apply description + hashtags, flash success, then close panel
    applyDescAndTags(cut) {
      const hashtags = this.editHashtagStr.split(',').map(t => t.trim()).filter(Boolean);
      this._updateCut(cut.id, { description: this.editDescription, hashtags });
      this.editSavedCutId = cut.id;
      setTimeout(() => {
        this.editingCutId = null;
        this.editSavedCutId = null;
      }, 1000);
    },

    // ─── Publish panel ────────────────────────────────────────────────────────

    togglePublishPanel(cut) {
      if (this.publishingCutId === cut.id) {
        this.publishingCutId = null;
        return;
      }
      this.editingCutId = null;
      this.publishingCutId = cut.id;
    },

    togglePublished(cut) {
      cut.published = !cut.published;
    },

    openPreview(cut) {
      this.previewCut = cut;
      this.$nextTick(() => {
        if (this.$refs.modalCutPreview) this.$refs.modalCutPreview.open();
      });
    },

    openShare(cut) {
      this.shareCut = cut;
      this.$nextTick(() => {
        if (this.$refs.modalSharePost) this.$refs.modalSharePost.open();
      });
    },

    // ─── Thumbnails ───────────────────────────────────────────────────────────

    async generateThumbnails() {
      if (!this.videoSrc || !this.cuts.length) return;

      const hiddenVideo = document.createElement('video');
      hiddenVideo.crossOrigin = 'anonymous'; // required for canvas.toDataURL on non-blob sources
      hiddenVideo.muted = true;
      hiddenVideo.src = this.videoSrc;

      try {
        await new Promise((resolve, reject) => {
          hiddenVideo.onloadedmetadata = resolve;
          hiddenVideo.onerror = reject;
          hiddenVideo.load();
        });
      } catch {
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 144;
      const ctx = canvas.getContext('2d');

      for (const cut of this.cuts) {
        try {
          await new Promise((resolve, reject) => {
            // If already at the target position some browsers skip the seeked event entirely
            if (Math.abs(hiddenVideo.currentTime - cut.start) < 0.001) {
              resolve();
              return;
            }
            hiddenVideo.onseeked = resolve;
            hiddenVideo.onerror = reject;
            hiddenVideo.currentTime = cut.start; // set AFTER handler to avoid missing fast seeks
          });
          ctx.drawImage(hiddenVideo, 0, 0, 256, 144);
          this.$set(this.thumbnails, cut.id, canvas.toDataURL('image/jpeg', 0.7));
        } catch {
          // skip — CORS or seek error
        }
      }

      hiddenVideo.src = '';
    },

    // ─── Import ───────────────────────────────────────────────────────────────

    onImportFile(e) {
      const files = Array.from(e.target.files);
      if (!files.length) return;
      e.target.value = '';

      const readers = files.map(
          file => new Promise((resolve, reject) => {
            const fr = new FileReader();
            fr.onload = ev => resolve(ev.target.result);
            fr.onerror = reject;
            fr.readAsText(file);
          })
      );

      Promise.all(readers)
          .then(results => {
            let totalAdded = 0;
            let totalSkipped = 0;
            results.forEach(text => {
              try {
                const { added, skippedCount } = this.processImport(JSON.parse(text));
                totalAdded += added;
                totalSkipped += skippedCount;
              } catch {
                totalSkipped++;
              }
            });
            this.importError = null;
            this.importSuccess = `Imported ${totalAdded} cut(s) from ${files.length} file(s)${totalSkipped ? `, skipped ${totalSkipped}` : ''}.`;
          })
          .catch(() => {
            this.importError = 'Failed to read file(s).';
          });
    },

    doImport() {
      this.importError = null;
      this.importSuccess = null;

      let parsed;
      try {
        parsed = JSON.parse(this.importText);
      } catch (err) {
        this.importError = 'Invalid JSON: ' + err.message;
        return;
      }

      const { added, skippedCount } = this.processImport(parsed);

      if (added > 0) {
        this.importSuccess = `Imported ${added} cut(s)${skippedCount ? `, skipped ${skippedCount}` : ''}.`;
        this.importText = '';
      } else {
        this.importError = `No valid cuts found — ${skippedCount} item(s) skipped (missing or invalid clip_start_sec/clip_end_sec, or end ≤ start).`;
      }
    },

    processImport(parsed) {
      let items = [];

      // Own export format { "cuts": [...] }
      if (parsed && Array.isArray(parsed.cuts)) {
        items = parsed.cuts.map(c => ({
          clip_start_sec: c.start,
          clip_end_sec: c.end,
          confidence: c.confidence,
          title: c.label,
          event_type: c.event_type,
          description: c.description,
          hashtags: c.hashtags,
          trigger_time: c.triggerTime,
          published: c.published,
        }));
      } else if (Array.isArray(parsed)) {
        items = parsed;
      } else if (parsed && typeof parsed === 'object') {
        items = [parsed];
      } else {
        return { added: 0, skippedCount: 1 };
      }

      let added = 0;
      let skippedCount = 0;
      const validConf = ['high', 'medium', 'low'];

      for (const item of items) {
        const start = typeof item.clip_start_sec === 'number'
            ? item.clip_start_sec
            : parseFloat(item.clip_start_sec);
        const end = typeof item.clip_end_sec === 'number'
            ? item.clip_end_sec
            : parseFloat(item.clip_end_sec);

        if (isNaN(start) || isNaN(end) || end <= start) {
          skippedCount++;
          continue;
        }

        this.addCut({
          start,
          end,
          label: item.title || '',
          confidence: validConf.includes(item.confidence) ? item.confidence : null,
          eventType: item.event_type || '',
          description: item.description || '',
          hashtags: Array.isArray(item.hashtags) ? item.hashtags : [],
          triggerTime: typeof item.trigger_time === 'number' ? item.trigger_time : null,
          published: Boolean(item.published),
          rejected: false,
        });
        added++;
      }

      return { added, skippedCount };
    },

    // ─── Project cuts (loaded from process_id param) ──────────────────────────

    async loadProcessType() {
      if (!this.processId) return;
      try {
        const res = await Content.getProcessDetails(this.processId);
        // console.log('[VideoEditor] loadProcessType raw:', res.data);
        // Handle { data: {...} }, { data: [{...}] }, or plain object/array
        const payload = (res.data && res.data.data != null) ? res.data.data : (res.data || {});
        const record  = Array.isArray(payload) ? payload[0] : payload;
        const type    = (record && record.type)   ? record.type   : null;
        const status  = (record && record.status) ? record.status : null;
        const s3Key   = (record && record.s3_key) ? record.s3_key : null;
        // console.log('[VideoEditor] process type resolved:', type, 'status:', status, 's3_key:', s3Key);
        this.processType   = type;
        this.processStatus = status;
        this.processS3Key  = s3Key;
        if (type) {
          const t = type.toLowerCase();
          this.cuttingMode = (t === 'auto' || t === 'ai') ? 'auto' : 'manual';
        }
      } catch (err) {
        console.warn('[VideoEditor] loadProcessType failed:', err);
      }
    },

    async loadVideoUrl() {
      if (!this.processId) return;
      clearTimeout(this.videoUrlRefreshTimer);
      try {
        const res = await Content.getVideoUrl(this.processId);
        const data = res.data || {};
        // Prefer a low-res proxy when the backend provides one
        const url = data.proxy_url || data.video_url;
        if (url) {
          this.videoSrc = url;
          // Presigned URLs expire — re-fetch 5 minutes before the TTL is up.
          // Default to 55 min if the API doesn't return expires_in.
          const ttlSeconds = data.expires_in || 3600;
          const refreshIn = Math.max((ttlSeconds - 300) * 1000, 60000);
          this.videoUrlRefreshTimer = setTimeout(() => this.loadVideoUrl(), refreshIn);
        }
      } catch {
        // Non-fatal: user can still upload a local file manually
      }
    },

    async loadProjectCuts() {
      if (!this.processId) return;
      this.loadingProjectCuts = true;
      this.projectCutsError = null;
      try {
        const res = await Content.getVideoCuts(this.processId);
        const raw = res.data.data || res.data || [];
        this.projectCuts = raw.map(c => ({
          ...c,
          id:        c.id        ?? c.cut_id ?? c.video_cut_id,
          label:     c.title     || c.label     || '',
          eventType: c.event_type || c.eventType || '',
          start:     c.clip_start_sec != null ? c.clip_start_sec : (c.start || 0),
          end:       c.clip_end_sec   != null ? c.clip_end_sec   : (c.end   || 0),
          hashtags:    Array.isArray(c.hashtags) ? c.hashtags : [],
          description: c.description || '',
          confidence:  c.confidence  || null,
          published:   Boolean(c.published),
          rejected:    false,
          crop_stats:  c.crop_stats || null,
          url_crop:    c.url_crop  || null,
          url_full:    c.url_full  || null,
        }));
      } catch (err) {
        this.projectCutsError = err?.response?.data?.error || 'Failed to load cuts.';
      } finally {
        this.loadingProjectCuts = false;
        this._invalidateBarCache();
        this._lastDrawTime = -1;
      }
    },

    openCropModal(cut) {
      this.cropEditCut = cut;
      this.$nextTick(() => {
        if (this.$refs.modalCropStats) this.$refs.modalCropStats.open();
      });
    },

    onCropSaved({ cutId, crop_stats }) {
      this._updateCut(cutId, { crop_stats });
      Swal.fire({
        icon: 'success',
        title: 'Crop Saved!',
        text: 'Crop settings have been saved for this cut.',
        confirmButtonColor: '#1D9E75',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    },

    openCropPreview(cut) {
      this.cropPreviewCut = cut;
      this.$nextTick(() => {
        if (this.$refs.modalPreviewCrop) this.$refs.modalPreviewCrop.open();
      });
    },

    async deleteCrop(cut) {
      const res = await Swal.fire({
        icon: 'warning',
        title: 'Delete crop?',
        text: 'The crop settings for this cut will be removed.',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#d9534f',
        cancelButtonText: 'Cancel',
      });
      if (!res.isConfirmed) return;
      this._updateCut(cut.id, { crop_stats: null });
    },

    formatCutTime(secs) {
      if (secs === null || secs === undefined || isNaN(secs)) return '—';
      const s = Math.floor(Number(secs));
      return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    },

    // ─── Type badge ───────────────────────────────────────────────────────────

    typeBadgeClass(type) {
      const map = {
        auto:   'bg-primary text-white',
        ai:     'bg-purple text-white',
        manual: 'bg-secondary text-white',
      };
      return map[(type || '').toLowerCase()] || 'bg-light text-dark border';
    },

    // ─── Confidence helpers ───────────────────────────────────────────────────

    confidenceColor(c) {
      const map = {
        high:   'rgba(29,158,117,0.6)',
        medium: 'rgba(216,90,48,0.65)',
        low:    'rgba(220,53,69,0.65)',
      };
      return map[c] || 'rgba(83,74,183,0.6)';
    },

    confidenceSolid(c) {
      const map = {
        high:   '#1D9E75',
        medium: '#D85A30',
        low:    '#DC3545',
      };
      return map[c] || '#534AB7';
    },

    confidenceBadgeStyle(c) {
      return {
        background: this.confidenceColor(c),
        color: '#fff',
        padding: '2px 6px',
        borderRadius: '3px',
        fontSize: '11px',
      };
    },

    // ─── Time helpers ─────────────────────────────────────────────────────────

    formatTime(seconds) {
      if (seconds === null || seconds === undefined || isNaN(seconds)) return '0:00';
      const s = Math.floor(seconds);
      return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
    },

    formatDuration(secs) {
      if (!secs || isNaN(secs) || secs < 0) return '0s';
      const s = Math.round(secs * 10) / 10;
      if (s < 60) return `${s}s`;
      const m = Math.floor(s / 60);
      const r = Math.round(s % 60);
      return `${m}m ${r}s`;
    },

    parseTimeStr(str) {
      if (str === null || str === undefined) return null;
      str = String(str).trim();
      if (/^\d+(\.\d+)?$/.test(str)) return parseFloat(str);
      const m = str.match(/^(\d+):(\d{1,2})(\.\d+)?$/);
      if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + (m[3] ? parseFloat(m[3]) : 0);
      return null;
    },
  },
};
</script>

<template>
  <Layout>
    <div class="ve-wrap">
      <!-- URL fetch progress bar -->
      <div v-if="urlLoading" class="ve-fetch-progress">
        <div class="ve-fetch-bar" :style="{ width: urlProgress + '%' }"></div>
      </div>

      <div class="ve-panels">

        <!-- ══════════════ LEFT PANEL ══════════════ -->
        <div class="ve-left">

          <!-- Video box -->
          <div class="ve-video-box">
            <video
              ref="video"
              class="ve-video"
              :src="videoSrc || undefined"
              preload="auto"
              controls
              @timeupdate="onTimeUpdate"
              @loadedmetadata="onLoadedMetadata"
              @ended="onVideoEnded"
              @play="isPlaying = true"
              @pause="isPlaying = false"
            ></video>
            <div v-if="!videoSrc" class="ve-no-video">
              <i class="mdi mdi-video-outline ve-no-video-icon"></i>
              <p class="mt-2 mb-0 small">No video loaded</p>
            </div>
          </div>

          <!-- Source row -->
          <div class="ve-source-row">
            <button v-if="!processId" class="btn btn-sm btn-primary" @click="$refs.fileInput.click()">
              <i class="mdi mdi-folder-open-outline"></i> Upload File
            </button>
            <input v-if="!processId" ref="fileInput" type="file" accept="video/*" class="d-none" @change="onFileChange" />

            <!-- Cutting mode -->
            <select v-model="cuttingMode" class="form-select form-select-sm ve-mode-select" :disabled="!!processId">
              <option value="manual">Manual cutting</option>
              <option value="auto">Auto cutting</option>
            </select>

            <button
                class="btn btn-sm btn-success"
                :disabled="(!processId && (!selectedFile || !cutsAllValid)) || uploading || processing"
                :title="processButtonTitle"
                @click="processVideo"
            >
              <i v-if="uploading || processing" class="mdi mdi-loading mdi-spin"></i>
              <i v-else-if="processId" class="mdi mdi-content-save-outline"></i>
              <i v-else class="mdi mdi-cloud-upload-outline"></i>
              {{ uploading ? 'Uploading ' + uploadProgress + '%' : processing ? 'Updating…' : processId ? 'Update' : 'Process' }}
            </button>

            <!-- URL fetch — hidden for now
            <input
              v-if="!processId"
              v-model="urlInput"
              type="text"
              class="form-control form-control-sm ve-url-input"
              placeholder="Paste video URL to fetch…"
              @keyup.enter="loadFromUrl"
            />
            <button
              v-if="!processId"
              class="btn btn-sm btn-outline-primary"
              :disabled="!urlInput.trim() || urlLoading"
              @click="loadFromUrl"
            >
              <i v-if="urlLoading" class="mdi mdi-loading mdi-spin"></i>
              <span v-else>Load</span>
            </button>
            -->
          </div>

          <!-- Do-not-close warning while upload / processing is in flight -->
          <div v-if="uploading || processing" class="alert alert-warning ve-alert py-2 px-3 small mb-0 d-flex align-items-center gap-2">
            <i class="mdi mdi-loading mdi-spin" style="font-size:16px; flex-shrink:0;"></i>
            <span>
              <strong>{{ uploading ? 'Uploading…' : 'Processing…' }}</strong>
              Please do not close or refresh this window until the operation completes.
            </span>
          </div>

          <!-- URL error -->
          <div v-if="urlError" class="alert alert-warning ve-alert py-1 px-2 small mb-0">
            <i class="mdi mdi-alert-outline"></i> {{ urlError }}
            <button type="button" class="btn-close float-end ms-2" style="font-size:10px;" @click="urlError = null"></button>
          </div>

          <!-- Time display — manual mode -->
          <div v-if="videoSrc && cuttingMode === 'manual'" class="ve-controls">
            <span class="ve-time-display">{{ formatTime(currentTime) }} / {{ formatTime(videoDuration) }}</span>
          </div>

          <!-- Time display in auto / AI mode (no cut buttons) -->
          <div v-if="videoSrc && cuttingMode === 'auto'" class="ve-controls">
            <span class="ve-time-display">{{ formatTime(currentTime) }} / {{ formatTime(videoDuration) }}</span>
          </div>

          <!-- Waveform — AnalyserNode/canvas, works for any file size.
               Fills in progressively as you play/scrub the video. -->
          <div class="ve-waveform-wrapper">
            <canvas ref="waveformCanvas" class="ve-waveform-canvas" @click="onWaveformClick"></canvas>
          </div>

          <!-- Thumbnail strip — disabled for now -->
          <div v-if="false" class="ve-thumb-strip">
            <div
              v-for="cut in cuts"
              :key="cut.id"
              class="ve-thumb-item"
              :class="{ 've-thumb-selected': selectedCutId === cut.id }"
              @click="selectCutAndSeek(cut)"
            >
              <img v-if="thumbnails[cut.id]" :src="thumbnails[cut.id]" width="128" height="72" />
              <div v-else class="ve-thumb-placeholder">
                <i class="mdi mdi-image-off-outline"></i>
              </div>
              <div class="ve-thumb-label">
                <div class="ve-thumb-label-title">{{ cut.label || ('Cut ' + cut.id) }}</div>
                <div class="ve-thumb-label-time">{{ formatTime(cut.start) }} → {{ formatTime(cut.end) }}</div>
              </div>
            </div>
          </div>

        </div><!-- /ve-left -->

        <!-- ══════════════ RIGHT PANEL ══════════════ -->
        <div class="ve-right" style="position:relative;">
          <div v-if="refreshing" class="ve-refresh-overlay">
            <b-spinner variant="primary"></b-spinner>
            <span class="ms-2 small text-muted">Refreshing…</span>
          </div>

          <!-- Cut list header -->
          <div class="ve-section-head">
            <span v-if="processId">
              Cuts
              <span class="badge bg-secondary ms-1">{{ projectCuts.length }}</span>
            </span>
            <span v-else-if="cuttingMode === 'manual'">
              Cuts <span class="badge bg-secondary ms-1">{{ cuts.length }}</span>
            </span>
            <span v-else>Auto cutting</span>
            <div class="d-flex gap-1">
              <button
                v-if="processId"
                class="btn btn-outline-secondary"
                style="font-size:11px; padding:1px 7px;"
                :disabled="loadingProjectCuts"
                @click="loadProjectCuts"
              >
                <b-spinner v-if="loadingProjectCuts" small></b-spinner>
                <i v-else class="mdi mdi-refresh"></i>
                Refresh
              </button>
              <button
                v-if="!processId && cuttingMode === 'manual' && cuts.length"
                class="btn btn-outline-danger"
                style="font-size:11px; padding:1px 7px;"
                @click="clearAllCuts"
              >Clear all</button>
            </div>
          </div>

          <div v-if="projectCutsError" class="alert alert-danger py-1 px-2 small m-2 mb-0">
            {{ projectCutsError }}
          </div>

          <!-- Auto / AI mode placeholder -->
          <div v-if="!processId && cuttingMode === 'auto'" class="ve-auto-info">
            <i class="mdi mdi-auto-fix mdi-36px"></i>
            <p class="mt-2 mb-1">Auto cutting enabled</p>
            <p class="small text-muted mb-0">The backend will automatically detect and cut clips from your video. Upload the file and click <strong>Process</strong>.</p>
          </div>

          <!-- Project cuts (when arriving from video-projects with a process_id) -->
          <div v-if="processId" ref="cutList">

            <div v-if="loadingProjectCuts && !projectCuts.length" class="text-center text-muted py-3 small">
              <b-spinner small class="me-1"></b-spinner> Loading cuts…
            </div>

            <div v-if="!loadingProjectCuts && !projectCuts.length" class="ve-empty-cuts">
              <i class="mdi mdi-scissors-cutting" style="font-size:32px; opacity:.3;"></i>
              <p class="mt-1 mb-0 small text-muted">No cuts found for this project.</p>
            </div>

            <div
              v-for="cut in projectCuts"
              :key="cut.id"
              :ref="'cut-card-' + cut.id"
              class="ve-cut-card"
              :class="{ 've-cut-selected': selectedCutId === cut.id, 've-cut-rejected': cut.rejected }"
              :style="selectedCutId !== cut.id ? { borderLeftColor: confidenceSolid(cut.confidence) } : {}"
            >
              <div class="row g-0">

                <!-- Info col (9/12) -->
                <div class="col-9 ve-cut-info">
                  <div class="ve-badge-row">
                    <span v-if="cut.type" class="badge" :class="typeBadgeClass(cut.type)">{{ cut.type }}</span>
                    <span v-if="cut.eventType" class="badge bg-primary text-white">{{ cut.eventType }}</span>
                    <span v-if="cut.published" class="badge bg-success">published</span>
                    <span v-if="cut.rejected" class="badge bg-secondary">rejected</span>
                  </div>

                  <input
                    v-model="cut.label"
                    type="text"
                    class="form-control form-control-sm ve-label-input"
                    placeholder="Title…"
                    :readonly="editingCutId !== cut.id"
                  />

                  <input
                    v-model="cut.eventType"
                    type="text"
                    class="form-control form-control-sm ve-event-type-input"
                    placeholder="Event type…"
                    :readonly="editingCutId !== cut.id"
                  />

                  <div class="ve-timestamps">
                    <span class="ve-ts-link" @click="seekTo(cut.start)" title="Seek to start">
                      <i class="mdi mdi-ray-start"></i>{{ formatTime(cut.start) }}
                    </span>
                    <span class="ve-ts-sep">→</span>
                    <span class="ve-ts-link" @click="seekTo(cut.end)" title="Seek to end">
                      {{ formatTime(cut.end) }}<i class="mdi mdi-ray-end"></i>
                    </span>
                    <span class="ve-ts-dur">{{ formatDuration(cut.end - cut.start) }}</span>
                  </div>

                  <transition name="ve-fade">
                    <div v-if="timeSetCutId === cut.id" class="alert alert-info ve-time-set-alert py-2 px-2 small mb-2">
                      <i class="mdi mdi-information-outline"></i>
                      <template v-if="timeSetField === 'start'">
                        Cut start set at <strong>{{ timeSetTime }}</strong> — seek to the end point then click "Set End".
                      </template>
                      <template v-else>
                        Cut end set at <strong>{{ timeSetTime }}</strong>.
                      </template>
                    </div>
                  </transition>

                  <p v-if="cut.description" class="ve-description">{{ cut.description }}</p>

                  <div v-if="cut.hashtags && cut.hashtags.length" class="ve-hashtags">
                    <span v-for="tag in cut.hashtags" :key="tag" class="badge bg-light text-dark border ve-tag">#{{ tag }}</span>
                  </div>

                  <div v-if="!cut.rejected && cut.crop_stats != null" class="ve-crop-added">
                    <span class="ve-crop-added-label"><i class="mdi mdi-check-circle"></i> Crop added</span>
                    <button type="button" class="ve-crop-link" @click="openCropPreview(cut)">
                      <i class="mdi mdi-eye-outline"></i> Preview crop
                    </button>
                    <button type="button" class="ve-crop-link ve-crop-link-danger" @click="deleteCrop(cut)">
                      <i class="mdi mdi-delete-outline"></i> Delete crop
                    </button>
                  </div>
                </div>

                <!-- Actions col (3/12) -->
                <div class="col-3 ve-cut-actions">
                  <button class="btn btn-sm btn-outline-primary w-100" :disabled="cut.rejected" @click="togglePlayCut(cut)">
                    <i :class="playingCutId === cut.id ? 'mdi mdi-stop' : 'mdi mdi-play'"></i>
                    {{ playingCutId === cut.id ? 'Stop' : 'Play' }}
                  </button>
                  <button class="btn btn-sm btn-outline-secondary w-100" :disabled="cut.rejected" @click="toggleEditCut(cut)">
                    <i class="mdi mdi-pencil-outline"></i> Edit
                  </button>
                  <button
                    v-if="isProcessCompleted"
                    class="btn btn-sm w-100"
                    :class="cut.published ? 'btn-success' : 'btn-outline-info'"
                    :disabled="cut.rejected || isManualProcess"
                    @click="togglePublishPanel(cut)"
                  >
                    <i :class="isManualProcess ? 'mdi mdi-check-outline' : 'mdi mdi-share-variant-outline'"></i>
                    {{ isManualProcess ? 'Accept' : 'Publish' }}
                  </button>
                  <button
                    class="btn btn-sm w-100"
                    :class="cut.rejected ? 'btn-outline-secondary' : (cut.crop_stats == null ? 'btn-danger' : 'btn-outline-success')"
                    :disabled="cut.rejected || cut.crop_stats != null"
                    @click="openCropModal(cut)"
                  >
                    <i class="mdi mdi-crop"></i> Crop<span v-if="!cut.rejected && cut.crop_stats == null"> !</span>
                  </button>
                  <button
                    v-if="cut.url_crop || cut.url_full"
                    class="btn btn-sm btn-outline-primary w-100"
                    @click="openPreview(cut)"
                  >
                    <i class="mdi mdi-play-circle-outline"></i> Preview
                  </button>
                  <button

                    class="btn btn-sm btn-outline-success w-100"
                    :disabled="cut.rejected"
                    @click="openShare(cut)"
                  >
                    <i class="mdi mdi-share-variant"></i> Share
                  </button>
                  <button
                    class="btn btn-sm w-100"
                    :class="cut.rejected ? 'btn-outline-secondary' : 'btn-outline-danger'"
                    @click="toggleRejectCut(cut)"
                  >
                    <i :class="cut.rejected ? 'mdi mdi-restore' : 'mdi mdi-cancel'"></i>
                    {{ cut.rejected ? 'Restore' : 'Reject' }}
                  </button>
                </div>
              </div><!-- /row -->

              <!-- Edit panel -->
              <div v-if="editingCutId === cut.id" class="ve-inline-panel">
                <div class="row g-2 mb-2">
                  <div class="col-6">
                    <label class="ve-field-label">Start — {{ formatTime(cut.start) }}</label>
                    <button class="btn btn-sm btn-primary w-100 mb-1" @click="applyCutTime(cut, 'start', currentTime)">
                      <i class="mdi mdi-ray-start"></i> Set to {{ formatTime(currentTime) }}
                    </button>
                    <input type="text" class="form-control form-control-sm" :value="formatTime(cut.start)" @change="applyCutTime(cut, 'start', $event.target.value)" placeholder="m:ss or seconds" />
                  </div>
                  <div class="col-6">
                    <label class="ve-field-label">End — {{ formatTime(cut.end) }}</label>
                    <button class="btn btn-sm btn-primary w-100 mb-1" @click="applyCutTime(cut, 'end', currentTime)">
                      <i class="mdi mdi-ray-end"></i> Set to {{ formatTime(currentTime) }}
                    </button>
                    <input type="text" class="form-control form-control-sm" :value="formatTime(cut.end)" @change="applyCutTime(cut, 'end', $event.target.value)" placeholder="m:ss or seconds" />
                  </div>
                </div>
                <label class="ve-field-label">Description</label>
                <textarea v-model="editDescription" class="form-control form-control-sm mb-2" rows="2"></textarea>
                <label class="ve-field-label">Hashtags <span class="text-muted fw-normal">(comma separated)</span></label>
                <input v-model="editHashtagStr" type="text" class="form-control form-control-sm mb-2" placeholder="free_kick, goal, 2024" />
                <div class="d-flex align-items-center justify-content-end gap-2">
                  <span v-if="editSavedCutId === cut.id" class="text-success small fw-semibold">
                    <i class="mdi mdi-check-circle-outline"></i> Saved!
                  </span>
                  <button class="btn btn-sm btn-primary" @click="applyDescAndTags(cut)">Apply</button>
                  <button class="btn btn-sm btn-outline-secondary ms-1" @click="editingCutId = null">Close</button>
                </div>
              </div>

              <!-- Accept / Publish panel -->
              <div v-if="publishingCutId === cut.id" class="ve-inline-panel">
                <div v-if="!isManualProcess" class="d-flex flex-wrap mb-2" style="gap:6px;">
                  <button class="btn btn-sm btn-outline-secondary" disabled title="Coming soon">
                    <i class="mdi mdi-music-note"></i> TikTok
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" disabled title="Coming soon">
                    <i class="mdi mdi-instagram"></i> Instagram
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" disabled title="Coming soon">
                    <i class="mdi mdi-youtube"></i> YouTube
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" disabled title="Coming soon">
                    <i class="mdi mdi-facebook"></i> Facebook
                  </button>
                </div>
                <button
                  class="btn btn-sm w-100"
                  :class="cut.published ? 'btn-success' : 'btn-outline-success'"
                  @click="togglePublished(cut)"
                >
                  <i :class="cut.published ? 'mdi mdi-check-circle-outline' : 'mdi mdi-circle-outline'"></i>
                  {{ cut.published ? (isManualProcess ? 'Mark as unaccepted' : 'Mark as unpublished') : (isManualProcess ? 'Mark as accepted' : 'Mark as published') }}
                </button>
              </div>

            </div><!-- /ve-cut-card -->
          </div>

          <!-- Cut cards (manual mode) -->
          <div v-if="cuttingMode === 'manual'" ref="cutList">

            <div v-if="!cuts.length" class="ve-empty-cuts">
              <i class="mdi mdi-scissors-cutting" style="font-size:32px; opacity:.3;"></i>
              <p class="mt-1 mb-0 small text-muted">No cuts yet. Click "Add Cut" to create a cut.</p>
            </div>

            <div
              v-for="cut in cuts"
              :key="cut.id"
              :ref="'cut-card-' + cut.id"
              class="ve-cut-card"
              :class="{ 've-cut-selected': selectedCutId === cut.id, 've-cut-rejected': cut.rejected }"
              :style="selectedCutId !== cut.id ? { borderLeftColor: confidenceSolid(cut.confidence) } : {}"
            >
              <div class="row g-0">

                <!-- Info col (9/12) -->
                <div class="col-9 ve-cut-info">
                  <div class="ve-badge-row">
                    <span class="badge" :style="confidenceBadgeStyle(cut.confidence)">
                      {{ cut.confidence || 'manual' }}
                    </span>
                    <span v-if="cut.eventType" class="badge bg-secondary">{{ cut.eventType }}</span>
                    <span v-if="cut.published" class="badge bg-success">published</span>
                    <span v-if="cut.rejected" class="badge bg-secondary">rejected</span>
                  </div>

                  <input
                    v-model="cut.label"
                    type="text"
                    class="form-control form-control-sm ve-label-input"
                    :class="{ 'is-invalid': !cut.rejected && (!cut.label || !cut.label.trim()) }"
                    placeholder="Title (required)…"
                  />

                  <input
                    v-model="cut.eventType"
                    type="text"
                    class="form-control form-control-sm ve-event-type-input"
                    :class="{ 'is-invalid': !cut.rejected && (!cut.eventType || !cut.eventType.trim()) }"
                    placeholder="Event type (required)…"
                  />

                  <div class="ve-timestamps">
                    <span class="ve-ts-link" @click="seekTo(cut.start)" title="Seek to start">
                      <i class="mdi mdi-ray-start"></i>{{ formatTime(cut.start) }}
                    </span>
                    <span class="ve-ts-sep">→</span>
                    <span class="ve-ts-link" @click="seekTo(cut.end)" title="Seek to end">
                      {{ formatTime(cut.end) }}<i class="mdi mdi-ray-end"></i>
                    </span>
                    <span class="ve-ts-dur">{{ formatDuration(cut.end - cut.start) }}</span>
                  </div>

                  <transition name="ve-fade">
                    <div v-if="timeSetCutId === cut.id" class="alert alert-info ve-time-set-alert py-2 px-2 small mb-2">
                      <i class="mdi mdi-information-outline"></i>
                      <template v-if="timeSetField === 'start'">
                        Cut start set at <strong>{{ timeSetTime }}</strong> — seek to the end point then click "Set End".
                      </template>
                      <template v-else>
                        Cut end set at <strong>{{ timeSetTime }}</strong>.
                      </template>
                    </div>
                  </transition>

                  <p v-if="cut.description" class="ve-description">{{ cut.description }}</p>

                  <div v-if="cut.hashtags && cut.hashtags.length" class="ve-hashtags">
                    <span v-for="tag in cut.hashtags" :key="tag" class="badge bg-light text-dark border ve-tag">#{{ tag }}</span>
                  </div>

                  <!-- Per-cut errors (only shown once a valid range is set) -->
                  <template v-if="cut.end > cut.start">
                  <div v-if="!cut.rejected && (!cut.label || !cut.label.trim())" class="alert alert-warning py-1 px-2 small mb-1 mt-1">
                    <i class="mdi mdi-alert-outline"></i> Title is required
                  </div>
                  <div v-if="!cut.rejected && (!cut.eventType || !cut.eventType.trim())" class="alert alert-warning py-1 px-2 small mb-1 mt-1">
                    <i class="mdi mdi-alert-outline"></i> Event type is required
                  </div>
                  <div v-if="!cut.rejected && cut.crop_stats == null" class="alert alert-danger py-1 px-2 small mb-1 mt-1">
                    <i class="mdi mdi-crop"></i> Crop settings are required
                  </div>
                  <div v-if="!cut.rejected && cut.crop_stats != null" class="ve-crop-added">
                    <span class="ve-crop-added-label"><i class="mdi mdi-check-circle"></i> Crop added</span>
                    <button type="button" class="ve-crop-link" @click="openCropPreview(cut)">
                      <i class="mdi mdi-eye-outline"></i> Preview crop
                    </button>
                    <button type="button" class="ve-crop-link ve-crop-link-danger" @click="deleteCrop(cut)">
                      <i class="mdi mdi-delete-outline"></i> Delete crop
                    </button>
                  </div>
                  </template>
                </div>

                <!-- Actions col (3/12) -->
                <div class="col-3 ve-cut-actions">
                  <template v-if="cut.end <= cut.start">
                  <button
                    class="btn btn-sm btn-outline-warning w-100"
                    :disabled="cut.rejected"
                    @click="applyCutTime(cut, 'start', $refs.video ? $refs.video.currentTime : currentTime)"
                  >
                    <i class="mdi mdi-ray-start"></i> Set Start
                  </button>
                  <button
                    class="btn btn-sm btn-outline-success w-100"
                    :disabled="cut.rejected"
                    @click="applyCutTime(cut, 'end', $refs.video ? $refs.video.currentTime : currentTime)"
                  >
                    <i class="mdi mdi-ray-end"></i> Set End
                  </button>
                  </template>
                  <template v-if="cut.end > cut.start">
                  <button class="btn btn-sm btn-outline-primary w-100" :disabled="cut.rejected" @click="togglePlayCut(cut)">
                    <i :class="playingCutId === cut.id ? 'mdi mdi-stop' : 'mdi mdi-play'"></i>
                    {{ playingCutId === cut.id ? 'Stop' : 'Play' }}
                  </button>
                  <button class="btn btn-sm btn-outline-secondary w-100" :disabled="cut.rejected" @click="toggleEditCut(cut)">
                    <i class="mdi mdi-pencil-outline"></i> Edit
                  </button>
                  <button
                    class="btn btn-sm w-100"
                    :class="cut.rejected ? 'btn-outline-secondary' : (cut.crop_stats == null ? 'btn-danger' : 'btn-outline-success')"
                    :disabled="cut.rejected || cut.crop_stats != null"
                    @click="openCropModal(cut)"
                  >
                    <i class="mdi mdi-crop"></i> Crop<span v-if="!cut.rejected && cut.crop_stats == null"> !</span>
                  </button>
                  </template>
                  <button
                    class="btn btn-sm w-100"
                    :class="cut.rejected ? 'btn-outline-secondary' : 'btn-outline-danger'"
                    @click="toggleRejectCut(cut)"
                  >
                    <i :class="cut.rejected ? 'mdi mdi-restore' : 'mdi mdi-cancel'"></i>
                    {{ cut.rejected ? 'Restore' : 'Reject' }}
                  </button>
                </div>
              </div><!-- /row -->

              <!-- Edit panel -->
              <div v-if="editingCutId === cut.id" class="ve-inline-panel">
                <div class="row g-2 mb-2">
                  <div class="col-6">
                    <label class="ve-field-label">Start — {{ formatTime(cut.start) }}</label>
                    <button
                      class="btn btn-sm btn-primary w-100 mb-1"
                      @click="applyCutTime(cut, 'start', currentTime)"
                    >
                      <i class="mdi mdi-ray-start"></i> Set to {{ formatTime(currentTime) }}
                    </button>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :value="formatTime(cut.start)"
                      @change="applyCutTime(cut, 'start', $event.target.value)"
                      placeholder="m:ss or seconds"
                    />
                  </div>
                  <div class="col-6">
                    <label class="ve-field-label">End — {{ formatTime(cut.end) }}</label>
                    <button
                      class="btn btn-sm btn-primary w-100 mb-1"
                      @click="applyCutTime(cut, 'end', currentTime)"
                    >
                      <i class="mdi mdi-ray-end"></i> Set to {{ formatTime(currentTime) }}
                    </button>
                    <input
                      type="text"
                      class="form-control form-control-sm"
                      :value="formatTime(cut.end)"
                      @change="applyCutTime(cut, 'end', $event.target.value)"
                      placeholder="m:ss or seconds"
                    />
                  </div>
                </div>
                <label class="ve-field-label">Description</label>
                <textarea
                  v-model="editDescription"
                  class="form-control form-control-sm mb-2"
                  rows="2"
                ></textarea>

                <label class="ve-field-label">Hashtags <span class="text-muted fw-normal">(comma separated)</span></label>
                <input
                  v-model="editHashtagStr"
                  type="text"
                  class="form-control form-control-sm mb-2"
                  placeholder="free_kick, goal, 2024"
                />

                <div class="d-flex align-items-center justify-content-end gap-2">
                  <span v-if="editSavedCutId === cut.id" class="text-success small fw-semibold">
                    <i class="mdi mdi-check-circle-outline"></i> Saved!
                  </span>
                  <button class="btn btn-sm btn-primary" @click="applyDescAndTags(cut)">Apply</button>
                  <button class="btn btn-sm btn-outline-secondary ms-1" @click="editingCutId = null">Close</button>
                </div>
              </div>

            </div><!-- /ve-cut-card -->

            <!-- Add Cut — always under the cuts -->
            <div v-if="!processId" class="p-2 mt-2 text-center">
              <button
                class="btn btn-primary"
                style="font-size:12px; padding:4px 10px; font-weight:600; letter-spacing:0.02em;"
                :disabled="cutsAddingLocked || !videoSrc"
                :title="cutsAddingLocked ? 'Cuts are managed automatically for this project' : !videoSrc ? 'Load a video first' : ''"
                @click="addNewCut"
              >
                <i class="mdi mdi-plus-circle-outline"></i> Add Cut
              </button>
            </div>
          </div><!-- /cutList -->

          <!-- Import panel -- COMMENTED FOR NOW BECAUSE IT IS ONLY USED IN DEVELOPMENT -->
<!--          <div class="ve-section-head ve-section-head-toggle" @click="showImport = !showImport">-->
<!--            <span>Import JSON</span>-->
<!--            <i :class="showImport ? 'mdi mdi-chevron-up' : 'mdi mdi-chevron-down'"></i>-->
<!--          </div>-->
<!--          <div v-if="showImport" class="ve-panel-body">-->
<!--            <div class="mb-2">-->
<!--              <button class="btn btn-sm btn-outline-secondary" @click="$refs.importFileInput.click()">-->
<!--                <i class="mdi mdi-file-upload-outline"></i> Upload .json file(s)-->
<!--              </button>-->
<!--              <input-->
<!--                ref="importFileInput"-->
<!--                type="file"-->
<!--                accept=".json,application/json"-->
<!--                class="d-none"-->
<!--                multiple-->
<!--                @change="onImportFile"-->
<!--              />-->
<!--            </div>-->
<!--            <textarea-->
<!--              v-model="importText"-->
<!--              class="form-control form-control-sm ve-import-textarea"-->
<!--              rows="5"-->
<!--              placeholder="Or paste JSON here…"-->
<!--            ></textarea>-->
<!--            <div v-if="importError" class="alert alert-danger py-1 px-2 small mt-1 mb-0">{{ importError }}</div>-->
<!--            <div v-if="importSuccess" class="alert alert-success py-1 px-2 small mt-1 mb-0">{{ importSuccess }}</div>-->
<!--            <button-->
<!--              class="btn btn-sm btn-primary mt-2"-->
<!--              :disabled="!importText.trim()"-->
<!--              @click="doImport"-->
<!--            >Import</button>-->
<!--          </div>-->

        </div><!-- /ve-right -->
      </div><!-- /ve-panels -->

      <!-- Project cuts modal -->
      <ModalEditCropStats
        ref="modalCropStats"
        :cut="cropEditCut"
        :video-url="videoSrc"
        @saved="onCropSaved"
      />

      <!-- Crop preview modal -->
      <ModalPreviewCrop
        ref="modalPreviewCrop"
        :cut="cropPreviewCut"
        :video-url="videoSrc"
      />

      <!-- Cut preview modal -->
      <ModalCutPreview
        ref="modalCutPreview"
        :cut="previewCut"
      />

      <!-- Share to social modal -->
      <ModalSharePost
        ref="modalSharePost"
        :cut="shareCut"
      />

    </div><!-- /ve-wrap -->
  </Layout>
</template>



<style scoped>
/* ── Wrapper ──────────────────────────────────────────────────────────────── */
.ve-wrap {
  /* Pull back the container-fluid (15px each side) + page-content gutter (10px each side) */
  margin: -20px -25px 0;
  height: calc(100vh - 70px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f4f5f7;
  color: #333;
  position: relative;
}

/* ── Fetch progress bar ───────────────────────────────────────────────────── */
.ve-fetch-progress {
  height: 3px;
  background: #dee2e6;
  flex-shrink: 0;
}
.ve-fetch-bar {
  height: 100%;
  background: #f0c040;
  transition: width 0.2s ease;
}

/* ── Two-panel split ──────────────────────────────────────────────────────── */
.ve-panels {
  display: flex;
  flex: 1;
  min-height: 0;
}

/* ── Left panel ───────────────────────────────────────────────────────────── */
.ve-left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  overflow: hidden;
  background: #fff;
}

.ve-video-box {
  position: relative;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
}
.ve-video {
  width: 100%;
  max-height: 440px;
  display: block;
  background: #000;
}


.ve-no-video {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  min-height: 180px;
  background: #e9ecef;
}
.ve-no-video-icon { font-size: 48px; }

.ve-source-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.ve-url-input {
  flex: 1;
}

.ve-mode-select {
  width: 160px;
  flex-shrink: 0;
}

.ve-auto-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  color: #495057;
  text-align: center;
  flex: 1;
}

.ve-alert { flex-shrink: 0; }

.ve-controls {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.ve-time-display {
  font-family: monospace;
  font-size: 13px;
  white-space: nowrap;
  color: #495057;
  min-width: 100px;
}
.ve-seekbar {
  flex: 1;
  accent-color: #f0c040;
  cursor: pointer;
}

/* ── Waveform ─────────────────────────────────────────────────────────────── */
.ve-waveform-wrapper {
  height: 80px;
  flex-shrink: 0;
  position: relative;
  background: #1a1a2e;
  border-radius: 6px;
  overflow: hidden;
}
.ve-waveform-canvas {
  width: 100%;
  height: 100%;
}
.ve-waveform-msg {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(255,255,255,0.45);
  z-index: 2;
  pointer-events: none;
}
.ve-waveform-warn { color: #f0c040; }

/* ── Thumbnail strip ──────────────────────────────────────────────────────── */
.ve-thumb-strip {
  display: flex;
  gap: 4px;
  overflow-x: auto;
  padding: 4px 2px;
  flex-shrink: 0;
  height: fit-content;
  background: #e9ecef;
  border-radius: 4px;
  align-items: flex-start;
}
.ve-thumb-item {
  flex-shrink: 0;
  cursor: pointer;
  border-radius: 3px;
  overflow: hidden;
  border: 2px solid transparent;
  transition: border-color 0.15s;
}
.ve-thumb-item:hover { border-color: rgba(240,192,64,0.5); }
.ve-thumb-selected  { border-color: #f0c040 !important; }
.ve-thumb-placeholder {
  width: 128px;
  height: 72px;
  background: #ced4da;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #868e96;
  font-size: 20px;
}
.ve-thumb-label {
  background: rgba(255,255,255,0.92);
  padding: 2px 5px;
  max-width: 128px;
}
.ve-thumb-label-title {
  font-size: 11px;
  font-weight: 600;
  color: #212529;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ve-thumb-label-time {
  font-size: 10px;
  color: #6c757d;
  white-space: nowrap;
  font-family: monospace;
}

/* ── Right panel ──────────────────────────────────────────────────────────── */
.ve-right {
  width: 600px;
  min-width: 600px;
  overflow-y: auto;
  border-left: 1px solid #2a2a3e;
  background: #fff;
  color: #333;
  display: flex;
  flex-direction: column;
}

.ve-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 5;
}
.ve-section-head-toggle { cursor: pointer; user-select: none; }
.ve-section-head-toggle:hover { background: #eef0f2; }

.ve-panel-body { padding: 10px 12px; }

/* ── Cut cards ────────────────────────────────────────────────────────────── */
.ve-cut-card {
  padding: 10px 12px;
  border-bottom: 1px solid #e9ecef;
  border-left: 4px solid transparent;
  transition: background 0.15s, border-left-color 0.15s, box-shadow 0.15s;
  cursor: pointer;
}
.ve-cut-card:hover {
  background: #f8f9fa;
  border-left-color: #dee2e6;
}
.ve-cut-selected {
  border-left-color: #f0c040 !important;
  background: rgba(240, 192, 64, 0.12) !important;
  box-shadow: inset 4px 0 0 #d4a800, 0 2px 6px rgba(240, 192, 64, 0.25) !important;
}
.ve-cut-selected:hover {
  background: rgba(240, 192, 64, 0.18) !important;
}

.ve-cut-rejected {
  opacity: 0.45;
  filter: grayscale(1);
  border-left-color: #adb5bd !important;
}
.ve-cut-rejected:hover {
  background: #f8f9fa;
}

.ve-cut-info { padding-right: 8px; }

.ve-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 5px;
}
.ve-label-input { margin-bottom: 4px; }
.ve-event-type-input { margin-bottom: 5px; color: #6c757d; }

.ve-timestamps {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
}
.ve-ts-link {
  color: #0d6efd;
  cursor: pointer;
  white-space: nowrap;
}
.ve-ts-link:hover { text-decoration: underline; }
.ve-ts-sep { color: #bbb; }
.ve-ts-dur { color: #888; }

/* "Crop added" confirmation row */
.ve-crop-added {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin: 4px 0;
  font-size: 12px;
}
.ve-crop-added-label {
  color: #1d9e75;
  font-weight: 600;
  white-space: nowrap;
}
.ve-crop-added-label .mdi { margin-right: 2px; }
.ve-crop-link {
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  color: #0d6efd;
  cursor: pointer;
  white-space: nowrap;
}
.ve-crop-link:hover { text-decoration: underline; }
.ve-crop-link .mdi { margin-right: 1px; }
.ve-crop-link-danger { color: #d9534f; }

/* Inline "Start/End set" confirmation banner */
.ve-time-set-alert {
  font-size: 12px;
  line-height: 1.35;
  margin-top: 2px;
}
.ve-time-set-alert .mdi { margin-right: 3px; }
.ve-fade-enter-active, .ve-fade-leave-active { transition: opacity 0.25s ease; }
.ve-fade-enter, .ve-fade-leave-to { opacity: 0; }

.ve-description {
  font-size: 12px;
  color: #555;
  margin-bottom: 4px;
  white-space: pre-wrap;
  word-break: break-word;
}
.ve-hashtags { display: flex; flex-wrap: wrap; gap: 3px; }
.ve-tag { font-size: 11px; }

.ve-cut-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-left: 1px solid #dee2e6;
  padding-left: 8px;
}
.ve-cut-actions .btn { font-size: 12px; }


.ve-inline-panel {
  margin-top: 10px;
  padding: 10px;
  background: #f1f3f5;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}
.ve-field-label {
  display: block;
  font-size: 11px;
  color: #666;
  margin-bottom: 2px;
  font-weight: 600;
}

.ve-empty-cuts {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
  text-align: center;
}

/* ── Refresh overlay ─────────────────────────────────────────────────────── */
.ve-refresh-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* ── Type badge ───────────────────────────────────────────────────────────── */
.bg-purple { background-color: #6f42c1 !important; }

/* ── Import / Export ──────────────────────────────────────────────────────── */
.ve-import-textarea {
  font-family: monospace;
  font-size: 11px;
  resize: vertical;
}

</style>
